import { action, atom } from '@reatom/framework'
import i18next from 'i18next'

import { reatomUpdatePremiumCurrency } from '@root/features/premium/update-currency'
import { DEMO_MODE, DomUtil, StringUtil } from '@root/shared'
import { atomWithPrev } from '@root/shared/lib'
import { showSnackbarAction } from '@root/shared/ui-kit'

import type { PremiumMetaModel } from '@root/entities/premium'

export const CURRENCY_IMAGE = {
  dimensions: { width: 512, height: 512 },
}

export const reatomUpdateLogoDrawer = ({
  premiumMeta,
}: {
  premiumMeta: PremiumMetaModel
}) => {
  const { updateCurrencyAction, updateRequestState, resetStateAction } =
    reatomUpdatePremiumCurrency()

  const imageAtom = atom<File | undefined>(undefined, 'imageAtom')

  const setImageAction = action(async (ctx, image: File) => {
    const img = await ctx.schedule(() => DomUtil.fileToImage(image))
    if (!img) {
      return false
    }
    const meetsDimensions =
      DEMO_MODE ||
      (img.width <= CURRENCY_IMAGE.dimensions.width &&
        img.height <= CURRENCY_IMAGE.dimensions.height)

    if (!meetsDimensions) {
      showSnackbarAction(ctx, {
        text: StringUtil.capitalize(
          i18next.t('premium.image-upload.size', {
            size: `${CURRENCY_IMAGE.dimensions.width}x${CURRENCY_IMAGE.dimensions.height}`,
          }),
        ),
        intent: 'error',
      })
      return
    }

    imageAtom(ctx, image)
  }, 'setImageAction')

  const uploadLogoInternalAction = action((ctx) => {
    const logo = ctx.get(imageAtom)
    const premiumId = ctx.get(premiumMeta.premiumId)
    if (!logo || !premiumId) {
      return
    }
    updateCurrencyAction(ctx, {
      logo,
      premiumId,
      currencyAddress: premiumMeta.currencyAddress,
    })
  }, 'uploadLogoInternalAction')

  const previewAtom = atomWithPrev<string | undefined>((ctx, previousImage) => {
    if (previousImage) {
      URL.revokeObjectURL(previousImage)
    }

    const image = ctx.spy(imageAtom)
    return image ? URL.createObjectURL(image) : undefined
  }, 'previewAtom')

  const clearImageAction = action((ctx) => {
    imageAtom(ctx, undefined)
  }, 'clearImageAction')

  const resetAction = action((ctx) => {
    imageAtom(ctx, undefined)
    resetStateAction(ctx)
  }, 'resetAction')

  return {
    updateRequestState,
    updateLogoAction: uploadLogoInternalAction,
    setImageAction,
    clearImageAction,
    imageAtom,
    previewAtom,
    resetAction,
  }
}
